// components/home/useClubApply.js
//
// Apply / withdraw logic for the Clubs tab (extracted from HomeScreen). Owns
// the in-flight club id + the synchronous applyLockRef double-tap guard, runs
// ClubService.applyToClub / cancelApplication against a FRESH application
// query (stale "not_pending" handling), refreshes Home's lists afterwards, and
// reports success through a Toast ("Applied to <club>" / "Application
// withdrawn") instead of a modal. Error and edge-case alerts stay modals.
// Returns { applyingClubId, applyBursts, onJoinPress, toast, hideToast };
// applyBursts = { [clubId]: count } bumped per confirmed submit (the card's
// mini confetti; per club and only ever increasing, so no other card fires).
// HomeScreen renders
// <Toast position="top" visible={toast.visible} message={toast.message} />.

import * as React from "react";
import { ClubService } from "../../services/clubService";
import { hasPendingApplication } from "../../services/clubApplicationStatus";

// `refresh` is called after every attempt that reached the server so the
// feed, memberships and pending-application list re-sync.
export default function useClubApply({ user, showAlert, refresh }) {
  const [applyingClubId, setApplyingClubId] = React.useState(null);
  const [toast, setToast] = React.useState({ visible: false, message: "" });
  const [applyBursts, setApplyBursts] = React.useState({});
  const applyLockRef = React.useRef(false);
  const refreshRef = React.useRef(refresh);
  refreshRef.current = refresh;

  const hideToast = React.useCallback(() => setToast((t) => ({ ...t, visible: false })), []);
  const showToast = (message) => setToast({ visible: true, message });

  // Returns an OUTCOME string ("submitted" / "canceled" / "not_pending") or
  // false; the caller maps it to the feedback to show. `message` only matters
  // on the submit branch (always "" today — the note sheet was removed, B5).
  const handleApplyToClub = async (clubId, message = "", { cancelOnly = false } = {}) => {
    // Synchronous re-entrancy lock: `applyingClubId` is React state, so a
    // double-tap (or two clubs tapped before the first commit) would both
    // pass a state-only guard and run concurrently — duplicate
    // applications, and one call's finally clearing the other's spinner.
    // The ref flips before any await. One application at a time.
    if (applyLockRef.current || applyingClubId) return false;
    applyLockRef.current = true;
    let succeeded = false;
    try {
      setApplyingClubId(clubId);
      if (!user) {
        showAlert("Error", "You must be logged in to apply to clubs.", { type: "error" });
        return false;
      }

      // A failed status query is not evidence of anything: throw, the catch
      // alerts and returns false (never "not_pending" / a submit off an error).
      const { data: hasPending, error: appsError } =
        await hasPendingApplication(user.uid, clubId);
      if (appsError) throw appsError;

      if (hasPending) {
        const { error } = await ClubService.cancelApplication(clubId, user.uid);
        if (error) throw error;
        succeeded = "canceled";
      } else if (cancelOnly) {
        // Direct "Applied" tap intends CANCELLATION ONLY. If the fresh
        // query finds nothing pending (reviewed/withdrawn elsewhere while
        // this screen showed stale state), falling through to the submit
        // branch would silently create a brand-new application the user
        // never asked for — report and let the refresh below fix the UI.
        succeeded = "not_pending";
      } else {
        const { error } = await ClubService.applyToClub(clubId, user.uid, message);
        if (error) {
          if (error.message === "You are already a member of this club") {
            showAlert("Already a Member", "You are already a member of this club!", {
              type: "warning",
            });
          } else {
            throw error;
          }
        } else {
          succeeded = "submitted";
        }
      }

      refreshRef.current?.();
    } catch (error) {
      if (__DEV__) console.error("Error applying to club:", error);
      showAlert("Error", error.message || "Failed to submit application. Please try again.", {
        type: "error",
      });
      succeeded = false;
    } finally {
      setApplyingClubId(null);
      applyLockRef.current = false;
    }
    return succeeded;
  };

  // Tap on a club's join button. "apply" submits directly (B5: no note
  // sheet); "applied" (tap to withdraw) is cancel-only.
  const onJoinPress = (club, joinState) => {
    if (!club.is_verified) return;
    if (joinState === "apply") {
      handleApplyToClub(club.id, "").then((outcome) => {
        if (outcome === "submitted") {
          setApplyBursts((prev) => ({ ...prev, [club.id]: (prev[club.id] || 0) + 1 }));
          showToast(`Applied to ${club.name}`);
        } else if (outcome === "canceled") {
          // Stale-state edge: a pending application already existed (e.g.
          // submitted on another device) — the tap withdrew it; say so.
          showAlert(
            "Application Canceled",
            "You already had a pending application for this club — it has been canceled.",
            { type: "warning" }
          );
        }
      });
    } else {
      handleApplyToClub(club.id, "", { cancelOnly: true }).then((outcome) => {
        if (outcome === "canceled") {
          showToast("Application withdrawn");
        } else if (outcome === "not_pending") {
          showAlert(
            "Already Reviewed",
            "This application is no longer pending — it was already reviewed or withdrawn. The list has been refreshed.",
            { type: "info" }
          );
        }
      });
    }
  };

  return { applyingClubId, applyBursts, onJoinPress, toast, hideToast };
}
